import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  invalidateConversationCollections,
  invalidateConversationDetail,
  patchConversationInCaches,
  removeConversationCaches,
} from '@/lib/queryInvalidation';
import { queryKeys } from '@/lib/queryKeys';
import {
  bulkDeleteConversations,
  createConversation,
  deleteConversation,
  selectConversation,
  updateConversation,
  type BulkDeleteResponse,
} from '@/api/conversations';
import type { Conversation } from '@/models/conversation';

export interface CreateConversationInput {
  title: string;
  agent_key?: string;
}

export interface UpdateConversationInput {
  conversationId: string;
  updates: Partial<Conversation>;
}

export interface DeleteConversationInput {
  conversationId: string;
}

export interface BulkDeleteConversationsInput {
  conversationIds: string[];
}

export interface SelectConversationInput {
  conversationId: string;
  previousConversationId?: string;
}

interface UpdateConversationOptimisticContext {
  previousList: Conversation[] | undefined;
  previousDetail: Conversation | undefined;
  previousSearches: Array<[readonly unknown[], unknown]>;
  userContext: unknown;
  optimisticRenameApplied: boolean;
}

export function useCreateConversationMutation(
  options?: UseMutationOptions<Conversation, Error, CreateConversationInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (input) => createConversation(input),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidateConversationCollections(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useUpdateConversationMutation(
  options?: UseMutationOptions<Conversation, Error, UpdateConversationInput>
) {
  const queryClient = useQueryClient();
  const {
    onMutate,
    onError,
    onSettled,
    onSuccess,
    ...mutationOptions
  } = options ?? {};

  return useMutation<Conversation, Error, UpdateConversationInput, UpdateConversationOptimisticContext>({
    ...mutationOptions,
    mutationFn: ({ conversationId, updates }) =>
      updateConversation(conversationId, updates),
    onMutate: async (variables, context) => {
      const userContext = await onMutate?.(variables, context);
      const optimisticRenameApplied = typeof variables.updates.title === 'string';

      if (!optimisticRenameApplied) {
        return {
          previousList: undefined,
          previousDetail: undefined,
          previousSearches: [],
          userContext,
          optimisticRenameApplied,
        };
      }

      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.conversations.list() }),
        queryClient.cancelQueries({
          queryKey: queryKeys.conversations.detail(variables.conversationId),
        }),
        queryClient.cancelQueries({ queryKey: queryKeys.conversations.searchRoot() }),
      ]);

      const previousList = queryClient.getQueryData<Conversation[]>(
        queryKeys.conversations.list()
      );
      const previousDetail = queryClient.getQueryData<Conversation>(
        queryKeys.conversations.detail(variables.conversationId)
      );
      const previousSearches = queryClient.getQueriesData({
        queryKey: queryKeys.conversations.searchRoot(),
      });

      const nextTitle = variables.updates.title as string;
      patchConversationInCaches(queryClient, variables.conversationId, (conversation) => ({
        ...conversation,
        title: nextTitle,
      }));

      return {
        previousList,
        previousDetail,
        previousSearches,
        userContext,
        optimisticRenameApplied,
      };
    },
    onError: async (error, variables, onMutateResult, context) => {
      if (onMutateResult?.optimisticRenameApplied) {
        queryClient.setQueryData(queryKeys.conversations.list(), onMutateResult.previousList);
        queryClient.setQueryData(
          queryKeys.conversations.detail(variables.conversationId),
          onMutateResult.previousDetail
        );

        onMutateResult.previousSearches.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }

      await onError?.(error, variables, onMutateResult?.userContext, context);
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      patchConversationInCaches(queryClient, variables.conversationId, () => data);
      await Promise.all([
        invalidateConversationCollections(queryClient),
        invalidateConversationDetail(queryClient, variables.conversationId),
      ]);
      await onSuccess?.(data, variables, onMutateResult?.userContext, context);
    },
    onSettled: async (data, error, variables, onMutateResult, context) => {
      await onSettled?.(data, error, variables, onMutateResult?.userContext, context);
    },
  });
}

export function useDeleteConversationMutation(
  options?: UseMutationOptions<Conversation[], Error, DeleteConversationInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({ conversationId }) => deleteConversation(conversationId),
    onSuccess: async (data, variables, onMutateResult, context) => {
      removeConversationCaches(queryClient, variables.conversationId);
      await invalidateConversationCollections(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useBulkDeleteConversationsMutation(
  options?: UseMutationOptions<BulkDeleteResponse, Error, BulkDeleteConversationsInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({ conversationIds }) => bulkDeleteConversations(conversationIds),
    onSuccess: async (data, variables, onMutateResult, context) => {
      variables.conversationIds.forEach((conversationId) => {
        removeConversationCaches(queryClient, conversationId);
      });
      await invalidateConversationCollections(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useSelectConversationMutation(
  options?: UseMutationOptions<
    { status: string; message: string },
    Error,
    SelectConversationInput
  >
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({ conversationId, previousConversationId }) =>
      selectConversation(conversationId, previousConversationId),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        invalidateConversationCollections(queryClient),
        invalidateConversationDetail(queryClient, variables.conversationId),
        variables.previousConversationId
          ? invalidateConversationDetail(queryClient, variables.previousConversationId)
          : Promise.resolve(),
      ]);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
